// src/components/store/sections/navbar/NavbarMobileAppStyle.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import {
  Menu,
  Search,
  Heart,
  ShoppingCart,
  User,
  Home,
  LayoutGrid,
  Flame,
  ShoppingBag,
  X,
  Loader2,
  Package,
  ArrowRight,
  ArrowLeft,
  Store,
  Sparkles,
  ChevronDown,
  Check,
  Camera,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import UserHeaderMenu from "@/components/store/UserHeaderMenu";
import CartHeaderButton from "@/components/store/CartHeaderButton";
import ShopCardsModal, { ShopItem } from "@/components/store/sections/shop/ShopCardsModal";
import VisualSearchModal from "./VisualSearchModal";
import { useNavbarBranding, NavbarBrandIdentity, NavbarBrandingConfig } from "./brandingConfig";

interface NavbarProps {
  subdomain?: string;
  storeName?: string;
  logo?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  storeTagline?: string;
  brandingConfig?: Partial<NavbarBrandingConfig>;
  isPreview?: boolean;
}

export default function NavbarMobileAppStyle({
  subdomain = "nextlook",
  storeName = "NextLook",
  logo,
  primaryColor = "#059669",
  secondaryColor = "#047857",
  storeTagline,
  brandingConfig,
}: NavbarProps) {
  const {
    branding,
    effectiveStoreName,
    effectiveTagline,
    effectiveLogo,
    effectivePrimaryColor,
    effectiveSecondaryColor,
    mainBarStyle,
    subBarStyle,
  } = useNavbarBranding({
    subdomain,
    storeName,
    storeTagline,
    logo,
    primaryColor,
    secondaryColor,
    brandingConfig,
  });

  storeName = effectiveStoreName || storeName;
  logo = effectiveLogo;
  primaryColor = effectivePrimaryColor;
  secondaryColor = effectiveSecondaryColor;
  const router = useRouter();
  const cartContext = useCart?.() as any;

  const totalItems =
    typeof cartContext?.totalItems === "number"
      ? cartContext.totalItems
      : typeof cartContext?.total_items === "number"
      ? cartContext.total_items
      : Array.isArray(cartContext?.cart)
      ? cartContext.cart.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 1), 0)
      : Array.isArray(cartContext?.cartItems)
      ? cartContext.cartItems.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 1), 0)
      : 0;

  // Shop / Outlet States
  const [shopsList, setShopsList] = useState<ShopItem[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopId, setSelectedShopId] = useState<string>("all");
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [isAllShopsModalOpen, setIsAllShopsModalOpen] = useState(false);
  const shopDropdownRef = useRef<HTMLDivElement>(null);

  // Search States
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isVisualSearchOpen, setIsVisualSearchOpen] = useState(false);

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  // Mobile Drawer State
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const shopParam = params.get("shop");
      if (shopParam) {
        setSelectedShopId(shopParam);
      } else {
        setSelectedShopId("all");
      }
    }
  }, []);

  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(e.target as Node)) {
        setIsShopMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Shops
  useEffect(() => {
    async function fetchShops() {
      setShopsLoading(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";
        const apiUrl = `${apiBase}/api/v1/stores/shops/`;

        const token =
          typeof window !== "undefined"
            ? localStorage.getItem("merchant_token") ||
              localStorage.getItem("token") ||
              localStorage.getItem("customer_token") ||
              ""
            : "";

        const res = await fetch(apiUrl, {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : data?.results || data?.data || data?.shops || [];
          setShopsList(list);
        }
      } catch (err) {
        console.error("Error fetching shops in NavbarMobileAppStyle:", err);
      } finally {
        setShopsLoading(false);
      }
    }
    fetchShops();
  }, [subdomain]);

  // Live Auto-Suggestion Search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const params = new URLSearchParams({ search: searchQuery.trim() });
        if (selectedShopId !== "all") {
          params.append("shop", selectedShopId);
        }

        const apiUrl = isLocal
          ? `http://${subdomain}.localhost:8000/api/v1/products/?${params.toString()}`
          : `/api/v1/products/?${params.toString()}`;

        const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data?.results || [];
          setSuggestions(list.slice(0, 6));
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error("Live search error:", err);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedShopId, subdomain]);

  const handleOpenCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const hiddenBtn = document.querySelector("#mobile-app-hidden-cart-btn button") as HTMLButtonElement;
    if (hiddenBtn) {
      hiddenBtn.click();
      return;
    }

    if (cartContext) {
      if (typeof cartContext.openCart === "function") cartContext.openCart();
      if (typeof cartContext.setIsCartOpen === "function") cartContext.setIsCartOpen(true);
      if (typeof cartContext.setIsOpen === "function") cartContext.setIsOpen(true);
      if (typeof cartContext.toggleCart === "function") cartContext.toggleCart();
    }

    window.dispatchEvent(new CustomEvent("openCartDrawer"));
    window.dispatchEvent(new CustomEvent("openCart"));
    window.dispatchEvent(new Event("openCartDrawer"));
  };

  const handleSelectShop = (shopId: string | number) => {
    setIsShopMenuOpen(false);
    setIsAllShopsModalOpen(false);
    setSelectedShopId(String(shopId));
    if (shopId === "all") {
      router.push("/products");
    } else {
      router.push(`/products?shop=${shopId}`);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
    setIsMobileSearchOpen(false);
    if (searchQuery.trim()) {
      const shopParam = selectedShopId !== "all" ? `&shop=${selectedShopId}` : "";
      router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}${shopParam}`);
    }
  };

  const resolveImgUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const currentSelectedShop = shopsList.find((s) => String(s.id) === selectedShopId);
  const currentShopLabel = currentSelectedShop ? currentSelectedShop.name : "All Outlets";

  return (
    <header
      className="sticky top-0 z-50 w-full pt-1.5 sm:pt-3 px-2 sm:px-6 select-none font-sans"
      style={{
        ["--nav-primary" as any]: primaryColor,
        ["--nav-secondary" as any]: secondaryColor,
      }}
    >
      <div id="mobile-app-hidden-cart-btn" className="hidden" aria-hidden="true">
        <CartHeaderButton />
      </div>

      {/* মেইন ফ্রেম */}
      <div
        style={mainBarStyle}
        className="max-w-7xl mx-auto bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-md relative overflow-visible"
      >
        {/* ১.A. MOBILE FULL-WIDTH SEARCH OVERLAY */}
        {isMobileSearchOpen && (
          <div className="absolute inset-0 z-50 bg-white px-3 flex items-center gap-2 rounded-t-2xl sm:rounded-t-3xl border-b border-slate-200 animate-in fade-in duration-150">
            <button
              type="button"
              onClick={() => {
                setIsMobileSearchOpen(false);
                setShowSuggestions(false);
              }}
              className="p-1.5 rounded-full hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <form
              onSubmit={handleSearchSubmit}
              className="flex-1 flex items-center h-10 border rounded-full px-3.5 bg-slate-50 border-slate-200"
            >
              <input
                ref={mobileSearchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, brands and categories..."
                className="flex-1 h-full text-xs text-slate-800 bg-transparent outline-none placeholder:text-slate-400"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setSuggestions([]);
                  }}
                  className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setIsMobileSearchOpen(false);
                  setIsVisualSearchOpen(true);
                }}
                title="Search by Photo or Camera"
                className="p-1.5 text-slate-500 hover:text-slate-800 cursor-pointer shrink-0"
              >
                <Camera className="w-4 h-4" style={{ color: primaryColor }} />
              </button>
              <button
                type="submit"
                style={{ color: primaryColor }}
                className="p-1.5 font-bold cursor-pointer shrink-0"
              >
                <Search className="w-4 h-4 stroke-[2.5]" />
              </button>
            </form>
          </div>
        )}

        {/* ১ম সারি */}
        <div className="flex items-center justify-between gap-2.5 sm:gap-6 px-3 sm:px-6 py-2 sm:py-3.5 border-b border-slate-100 rounded-t-2xl sm:rounded-t-3xl relative z-30 min-h-[50px] sm:min-h-[58px]">
          {/* বাম পাশ */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="p-1.5 sm:p-2 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              aria-label="Open Navigation Menu"
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <NavbarBrandIdentity
              href="/"
              branding={branding}
              storeName={storeName}
              tagline={effectiveTagline}
              logo={logo}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              isDarkTheme={false}
              variantStyle="mobile_app"
            />

            {/* Desktop Outlet Selector Dropdown */}
            <div ref={shopDropdownRef} className="relative hidden xl:flex items-center ml-2">
              <button
                type="button"
                onClick={() => setIsShopMenuOpen(!isShopMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-300 bg-slate-50 text-slate-700 text-xs font-bold transition-all cursor-pointer shadow-2xs"
                title="Select Outlet"
              >
                <Store className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                <span className="truncate max-w-[95px]">{currentShopLabel}</span>
                <ChevronDown
                  className={`w-3 h-3 text-slate-400 transition-transform ${
                    isShopMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isShopMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-slate-100 z-[999] py-1.5 text-slate-800 animate-in fade-in zoom-in-95 duration-150">
                  <div className="px-3.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                    Select Shop / Outlet
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSelectShop("all")}
                    className={`w-full text-left px-3.5 py-2 text-xs font-bold hover:bg-slate-50 flex items-center justify-between cursor-pointer ${
                      selectedShopId === "all" ? "bg-slate-100 font-black" : ""
                    }`}
                    style={selectedShopId === "all" ? { color: primaryColor } : undefined}
                  >
                    <span>🏪 Main Store (All)</span>
                    {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
                  </button>

                  {shopsLoading ? (
                    <div className="px-3.5 py-2.5 text-xs text-slate-400 flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Loading outlets...</span>
                    </div>
                  ) : shopsList.length === 0 ? (
                    <div className="px-3.5 py-2 text-xs text-slate-400 font-medium">
                      No other outlets found
                    </div>
                  ) : (
                    shopsList.map((shop) => (
                      <button
                        key={shop.id}
                        type="button"
                        onClick={() => handleSelectShop(shop.id)}
                        className={`w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center justify-between cursor-pointer truncate ${
                          String(shop.id) === selectedShopId
                            ? "bg-slate-100 font-bold"
                            : "text-slate-700"
                        }`}
                        style={
                          String(shop.id) === selectedShopId ? { color: primaryColor } : undefined
                        }
                      >
                        <div className="truncate">
                          <span className="truncate block font-bold">{shop.name}</span>
                          {shop.category_label && (
                            <span className="text-[9px] text-slate-400 font-mono block">
                              {shop.category_label}
                            </span>
                          )}
                        </div>
                        {String(shop.id) === selectedShopId && (
                          <Check className="w-3.5 h-3.5 shrink-0" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Desktop "Shops" Story Trigger Pill */}
            <div className="hidden lg:flex items-center shrink-0">
              <button
                type="button"
                onClick={() => setIsAllShopsModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-purple-200 bg-purple-50/70 hover:bg-purple-100 text-purple-900 transition-all cursor-pointer group shadow-2xs"
                title="Explore Brand Shops"
              >
                <Sparkles className="w-3.5 h-3.5 text-purple-600 animate-pulse" />
                <span className="text-xs font-black">Shops</span>
              </button>
            </div>
          </div>

          {/* মাঝখান: ডেস্কটপ সার্চ বার */}
          <div ref={searchBoxRef} className="flex-1 max-w-2xl hidden md:flex items-center relative">
            <form onSubmit={handleSearchSubmit} className="w-full flex items-center relative">
              <button
                type="submit"
                className="absolute left-3.5 p-1 text-slate-400 hover:opacity-80 transition-opacity cursor-pointer"
                style={{ color: primaryColor }}
                title="Search"
              >
                <Search className="w-4 h-4" />
              </button>

              <input
                type="text"
                value={searchQuery}
                onFocus={() => {
                  if (suggestions.length > 0) setShowSuggestions(true);
                }}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, brands and categories..."
                className="w-full bg-[#f4f7fb] hover:bg-[#eef2f7] focus:bg-white text-xs sm:text-sm text-slate-800 placeholder-slate-400 pl-11 pr-28 py-2.5 rounded-full border border-slate-200/60 focus:outline-hidden transition-all shadow-inner"
                style={{ borderColor: searchQuery ? `${primaryColor}60` : undefined }}
              />

              {/* ক্লিয়ার বাটন, ক্যামেরা ও সার্চ */}
              <div className="absolute right-2.5 flex items-center gap-1">
                {isSearching ? (
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                ) : searchQuery ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setSuggestions([]);
                    }}
                    className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : null}

                {/* Search Bar Camera Button */}
                <button
                  type="button"
                  onClick={() => {
                    setShowSuggestions(false);
                    setIsVisualSearchOpen(true);
                  }}
                  title="Search by Photo or Camera"
                  className="p-1.5 rounded-full hover:bg-slate-200/70 text-slate-500 transition-colors cursor-pointer"
                >
                  <Camera className="w-4 h-4" style={{ color: primaryColor }} />
                </button>

                <button
                  type="submit"
                  style={{ backgroundColor: primaryColor }}
                  className="px-3 py-1 text-white text-[11px] font-bold rounded-full hover:opacity-90 active:scale-95 transition-all cursor-pointer shadow-xs"
                >
                  Search
                </button>
              </div>
            </form>

            {/* লাইভ ড্রপডাউন সাজেশন পপআপ */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-[999] text-slate-900 animate-in fade-in slide-in-from-top-1 duration-150">
                <div className="p-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider px-3">
                  <span>Matching Products</span>
                  <span>{suggestions.length} results</span>
                </div>

                <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                  {suggestions.map((item) => {
                    const img = resolveImgUrl(item.primary_image || item.image);
                    return (
                      <Link
                        key={item.id}
                        href={`/products/${item.slug || item.id}`}
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-center gap-3 p-2.5 hover:bg-slate-50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-slate-100 relative overflow-hidden shrink-0 border border-slate-200">
                          {img ? (
                            <img src={img} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5 text-slate-400 m-auto mt-2.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-xs font-bold text-slate-800 truncate">{item.name}</h5>
                          <p className="text-[10px] text-slate-400 line-clamp-1">
                            {item.category_name || "General"}
                          </p>
                        </div>
                        <div
                          style={{ color: primaryColor }}
                          className="text-xs font-black shrink-0"
                        >
                          ৳{Math.round(item.price).toLocaleString()}
                        </div>
                      </Link>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={handleSearchSubmit}
                  style={{ color: primaryColor }}
                  className="w-full py-2.5 bg-slate-50 hover:bg-slate-100 text-center text-xs font-bold border-t border-slate-100 flex items-center justify-center gap-1 cursor-pointer"
                >
                  <span>View all search results for &quot;{searchQuery}&quot;</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* ডান পাশ (এখান থেকে Visual Search বাটন পুরোপুরি সরিয়ে দেওয়া হয়েছে) */}
          <div className="flex items-center gap-1 sm:gap-2.5 shrink-0 relative z-50 [&_button[aria-label='Visual_Search']]:hidden">
            <style>{`
              header button[aria-label="Visual Search"] {
                display: none !important;
              }
            `}</style>

            {/* Mobile Search Trigger Icon Button */}
            <button
              type="button"
              onClick={() => setIsMobileSearchOpen(true)}
              className="md:hidden p-1.5 rounded-full text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              title="Search"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            <Link
              href="/wishlist"
              className="hidden sm:flex p-2 rounded-full text-slate-700 hover:bg-slate-100 hover:text-rose-500 transition-colors"
              title="Wishlist"
            >
              <Heart className="w-5 h-5 stroke-[1.8]" />
            </Link>

            <button
              type="button"
              onClick={handleOpenCart}
              className="p-1.5 sm:p-2 relative text-slate-700 hover:bg-slate-100 hover:text-slate-900 rounded-full transition-colors cursor-pointer"
              title="Shopping Cart"
            >
              <ShoppingCart className="w-5 h-5 stroke-[1.8]" />
              {totalItems > 0 && (
                <span
                  style={{ backgroundColor: primaryColor }}
                  className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-white text-[9px] font-black flex items-center justify-center shadow-xs"
                >
                  {totalItems}
                </span>
              )}
            </button>

            <div className="relative z-50">
              <UserHeaderMenu />
            </div>
          </div>
        </div>

        {/* ২য় সারি: Mobile App Style Tab Bar */}
        <nav
          style={subBarStyle}
          className="flex items-center justify-around sm:justify-center sm:gap-12 md:gap-16 py-2 sm:py-2.5 px-2 bg-white rounded-b-2xl sm:rounded-b-3xl relative z-20 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          <Link
            href="/"
            className="flex flex-col items-center gap-0.5 group cursor-pointer transition-colors shrink-0"
            style={{ color: primaryColor }}
          >
            <Home className="w-4 h-4 fill-current" />
            <span className="text-[10px] sm:text-[11px] font-black">Home</span>
          </Link>

          <Link
            href="/categories"
            className="flex flex-col items-center gap-0.5 text-slate-600 hover:text-slate-900 group cursor-pointer transition-colors shrink-0"
          >
            <LayoutGrid className="w-4 h-4 text-slate-600 group-hover:text-slate-900 transition-colors" />
            <span className="text-[10px] sm:text-[11px] font-bold">Categories</span>
          </Link>

          <button
            type="button"
            onClick={() => setIsAllShopsModalOpen(true)}
            className="flex flex-col items-center gap-0.5 text-purple-700 hover:text-purple-900 group cursor-pointer transition-colors shrink-0"
          >
            <Sparkles className="w-4 h-4 text-purple-600 animate-pulse" />
            <span className="text-[10px] sm:text-[11px] font-black flex items-center gap-0.5">
              <span>Shops</span>
              <span className="w-1 h-1 rounded-full bg-emerald-500" />
            </span>
          </button>

          <Link
            href="/products?deals=true"
            className="flex flex-col items-center gap-0.5 text-slate-600 hover:text-slate-900 group cursor-pointer transition-colors shrink-0"
          >
            <Flame className="w-4 h-4 text-slate-600 group-hover:text-slate-900 transition-colors" />
            <span className="text-[10px] sm:text-[11px] font-bold">Deals</span>
          </Link>

          <Link
            href="/dashboard"
            className="flex flex-col items-center gap-0.5 text-slate-600 hover:text-slate-900 group cursor-pointer transition-colors shrink-0"
          >
            <User className="w-4 h-4 text-slate-600 group-hover:text-slate-900 transition-colors" />
            <span className="text-[10px] sm:text-[11px] font-bold">Account</span>
          </Link>
        </nav>
      </div>

      {/* ৩. SHOP CARDS MODAL CALL */}
      <ShopCardsModal
        isOpen={isAllShopsModalOpen}
        onClose={() => setIsAllShopsModalOpen(false)}
        shopsList={shopsList}
        shopsLoading={shopsLoading}
        onSelectShop={handleSelectShop}
        primaryColor={primaryColor}
      />

      {/* ৩.৫ AI VISUAL SEARCH MODAL */}
      <VisualSearchModal
        isOpen={isVisualSearchOpen}
        onClose={() => setIsVisualSearchOpen(false)}
        subdomain={subdomain}
        primaryColor={primaryColor}
      />

      {/* ৪. মোবাইল সাইডবার মেনু ড্রয়ার */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[999999] flex">
          <div
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity"
            onClick={() => setDrawerOpen(false)}
          />

          <div className="relative w-72 max-w-[80vw] bg-white h-full shadow-2xl p-5 flex flex-col justify-between z-10 animate-in slide-in-from-left duration-300">
            <div className="space-y-5 overflow-y-auto max-h-[85vh]">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2 font-black text-lg text-slate-900">
                  <ShoppingBag className="w-5 h-5" style={{ color: primaryColor }} />
                  <span>
                    {storeName?.slice(0, 4) || "Next"}
                    <span style={{ color: primaryColor }}>{storeName?.slice(4) || "Look"}</span>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {shopsList.length > 0 && (
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">
                    Shops & Outlets
                  </h4>
                  <div className="space-y-1 text-xs font-bold text-slate-700">
                    <button
                      type="button"
                      onClick={() => {
                        handleSelectShop("all");
                        setDrawerOpen(false);
                      }}
                      className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left ${
                        selectedShopId === "all" ? "bg-slate-100 font-black" : "hover:bg-slate-50"
                      }`}
                      style={selectedShopId === "all" ? { color: primaryColor } : undefined}
                    >
                      <span>🏪 Main Store (All)</span>
                      {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
                    </button>
                    {shopsList.map((shop) => (
                      <button
                        key={shop.id}
                        type="button"
                        onClick={() => {
                          handleSelectShop(shop.id);
                          setDrawerOpen(false);
                        }}
                        className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left ${
                          String(shop.id) === selectedShopId
                            ? "bg-slate-100 font-black"
                            : "hover:bg-slate-50"
                        }`}
                        style={
                          String(shop.id) === selectedShopId ? { color: primaryColor } : undefined
                        }
                      >
                        <div className="truncate">
                          <span className="block font-bold">{shop.name}</span>
                          {shop.category_label && (
                            <span className="text-[10px] text-slate-400 font-normal">
                              {shop.category_label}
                            </span>
                          )}
                        </div>
                        {String(shop.id) === selectedShopId && (
                          <Check className="w-3.5 h-3.5 shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <nav className="space-y-1 font-bold text-xs text-slate-700 border-t border-slate-100 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setDrawerOpen(false);
                    setIsAllShopsModalOpen(true);
                  }}
                  className="w-full flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-100 text-left font-black text-purple-700"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <span>All Brand Shops</span>
                  </div>
                  <ArrowRight className="w-4 h-4" />
                </button>

                <Link
                  href="/"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-slate-100"
                  style={{ color: primaryColor }}
                >
                  <Home className="w-4 h-4" /> Home
                </Link>
                <Link
                  href="/categories"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-slate-100"
                >
                  <LayoutGrid className="w-4 h-4" /> All Categories
                </Link>
                <Link
                  href="/products?deals=true"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-slate-100"
                >
                  <Flame className="w-4 h-4" /> Hot Deals & Offers
                </Link>
                <Link
                  href="/wishlist"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-slate-100"
                >
                  <Heart className="w-4 h-4" /> My Wishlist
                </Link>
                <Link
                  href="/dashboard"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-slate-100"
                >
                  <User className="w-4 h-4" /> My Account
                </Link>
              </nav>
            </div>

            <div className="pt-3 border-t border-slate-100 text-[10px] text-slate-400 font-mono text-center">
              © {storeName} • Mobile App Navigation
            </div>
          </div>
        </div>
      )}
    </header>
  );
}